import { faBarsFilter, faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { useMutation } from '@tanstack/react-query';
import {
  Action,
  cn,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RelativeDateTime,
  SimpleTooltip,
  SpinnerIcon,
} from 'corso-admin-ui';
import { ALERT_UNREAD_WINDOW_DAYS, CrewMerchantUi } from 'corso-types';
import { subDays } from 'date-fns';
import { PropsWithChildren, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import api from '~/api';
import { useUserId } from '~/features/settings/developer/hooks/useUserId';
import useStoreAlerts from '~/hooks/useStoreAlerts';
import { useStoreId } from '~/providers/StoreUserProvider';

/**
 * With a `to` value, will render as a `NavLink`; otherwise, will just render as a `div`.
 * Any link to a different origin will open in a new tab.
 */
function PossibleLink({
  to,
  className,
  children,
}: PropsWithChildren<{ to?: string | null; className?: string }>) {
  if (to) {
    const isLocal = new URL(to).origin === window.location.origin;

    return (
      <NavLink
        to={to}
        target={isLocal ? undefined : '_blank'}
        className={className}
      >
        {children}
      </NavLink>
    );
  }
  return <div className={className}>{children}</div>;
}

function StoreAlert({
  alert,
  showUnreadOnly,
}: {
  alert: CrewMerchantUi.StoreAlert;
  showUnreadOnly?: boolean;
}) {
  const { refetch } = useStoreAlerts();
  const storeId = useStoreId();
  const userId = useUserId();

  const { mutate: toggleRead, isPending: isToggleReadPending } = useMutation({
    mutationFn: async () =>
      api
        .store(storeId)
        .userAlerts(userId)
        .toggleRead(`${alert.id}`, {
          readOn: alert.readOn ? null : new Date().toISOString(),
        }),
    onSuccess: () => refetch(),
  });

  const isUnread = alert.readOn === null;
  const isImplicitlyRead =
    new Date(alert.createdOn) < subDays(new Date(), ALERT_UNREAD_WINDOW_DAYS);

  return (
    <li
      key={alert.id}
      hidden={showUnreadOnly && !isUnread}
      className="group bg-white p-1 focus-within:has-[[href]]:bg-neutral-50 has-[[href]]:hover:bg-neutral-50"
    >
      <PossibleLink
        className="grid grid-cols-[auto_1fr] items-baseline gap-2 rounded-md p-2 focus:ring-2 focus:ring-corso-blue-400 focus:outline-none"
        to={alert.url}
      >
        {alert.severity === 'Info' ? (
          <Icon
            icon="dot"
            className={cn(
              'size-2 px-0.5 text-corso-blue-400',
              !isUnread && 'invisible',
            )}
          />
        ) : (
          <Icon
            icon="warning"
            className={cn('size-3 text-amber-500', !isUnread && 'invisible')}
          />
        )}
        <div>
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
              <p className="text-xs text-neutral-500">
                {alert.category} &bull;{' '}
                <RelativeDateTime dateTime={alert.createdOn} />
              </p>
            </div>

            <SimpleTooltip
              asChild
              content={isUnread ? 'Mark as read' : 'Mark as unread'}
            >
              <Action
                onClick={(e) => {
                  e.preventDefault();
                  toggleRead();
                }}
                variant="ghost"
                icon={isUnread ? faCircleCheck : 'dot'}
                loading={isToggleReadPending}
                accessibilityLabel={
                  isUnread ? 'Mark as read' : 'Mark as unread'
                }
                // 🔮 this is magic, preserve the element in the DOM for tab-navigation, and make it visible only with focus or hover
                className="pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
                hidden={isImplicitlyRead} // hide toggle after cutoff to prevent marking old alerts as unread, as they implicitly are considered ready after
              />
            </SimpleTooltip>
          </div>
          <p className={cn('mb-1 text-xs', isUnread && 'font-medium')}>
            {alert.subject}
          </p>
          <p className="text-xs text-pretty text-neutral-500">
            {alert.message}
          </p>
        </div>
      </PossibleLink>
    </li>
  );
}

export default function StoreAlerts({
  setIsAlertOpen,
  isAlertOpen,
}: {
  setIsAlertOpen: (isOpen: boolean) => void;
  isAlertOpen: boolean;
}) {
  const storeId = useStoreId();
  const userId = useUserId();
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);

  const { data, isPending, isError, refetch } = useStoreAlerts();

  const { mutate: markAllRead, isPending: isMarkAllReadPending } = useMutation({
    mutationFn: () =>
      api
        .store(storeId)
        .userAlerts(userId)
        .markAllRead({ readOn: new Date().toISOString() }),
    onSuccess: () => refetch(),
  });

  const hasUnread = !!data?.some((alert) => alert.readOn === null);
  const headerRef = useRef<HTMLDivElement>(null);

  return (
    <Popover open={isAlertOpen} onOpenChange={setIsAlertOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group relative flex size-8 items-center justify-center rounded-full bg-neutral-900 p-1 focus-within:bg-neutral-800 hover:bg-neutral-800 data-[state=open]:bg-neutral-800"
        >
          <span className="sr-only">Alerts</span>
          <Icon
            icon="bell"
            className="text-white group-data-[state=open]:hidden"
          />
          <Icon
            icon="bellSolid"
            className="hidden text-white group-data-[state=open]:block"
          />
          {hasUnread && (
            <div className="absolute top-2 right-2 size-2 rounded-full bg-red-400" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="relative min-w-[95vw] p-0 md:min-w-sm"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          headerRef.current?.focus();
        }}
      >
        <header
          tabIndex={-1}
          className="sticky top-0 flex items-center justify-between gap-1 border-b border-b-neutral-200 bg-white p-3"
          ref={headerRef}
        >
          <p className="text-sm font-medium">Alerts</p>
          <div className="flex items-center gap-1">
            <SimpleTooltip
              asChild
              content={showUnreadOnly ? 'View all' : 'View unread only'}
            >
              <Action
                onClick={() => setShowUnreadOnly((current) => !current)}
                variant="ghost"
                icon={faBarsFilter}
                accessibilityLabel={
                  showUnreadOnly ? 'View all' : 'View unread only'
                }
                pressed={showUnreadOnly}
              />
            </SimpleTooltip>
            <SimpleTooltip asChild content="Mark all as read">
              <Action
                onClick={() => markAllRead()}
                variant="ghost"
                icon={faCircleCheck}
                accessibilityLabel="Mark all as read"
                disabled={!hasUnread}
                loading={isMarkAllReadPending}
              />
            </SimpleTooltip>
          </div>
        </header>
        {isPending && (
          <p className="p-3 text-center">
            <span className="sr-only">Loading Alerts</span>
            <SpinnerIcon />
          </p>
        )}
        {/* // TODO verify error handling when parsed */}
        {isError && (
          <p className="flex items-center justify-center gap-1 p-3 text-xs font-semibold text-red-500">
            <Icon icon="errorInline" />
            Unable to load alerts
          </p>
        )}
        {!isPending && !isError && (
          <ul className="flex flex-col gap-px bg-neutral-200">
            {data.map((alert) => (
              <StoreAlert
                key={alert.id}
                alert={alert}
                showUnreadOnly={showUnreadOnly}
              />
            ))}
            <li className="bg-white p-3 text-center text-xs font-semibold text-neutral-500">
              <p>
                {showUnreadOnly ? 'No more unread alerts' : `No more alerts`}
              </p>
            </li>
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
